import { api, ApiError, setToken } from '../api.ts';
import {
  DEFAULT_KDF_PARAMS,
  deriveAuthSecret,
  deriveEncryptionKey,
  deriveMasterKey,
  deriveRecoveryAuthSecret,
  deriveRecoveryEncryptionKey,
  deriveRecoveryMasterKey,
  generateRecoveryCode,
  generateSalt,
  type KdfParams,
} from './kdf.ts';
import {
  generateKeyPair,
  importPrivateKey,
  selectAlgorithm,
  unwrapBytes,
  unwrapPrivateKey,
  wrapBytesFor,
  wrapPrivateKey,
  type KeyAlgorithm,
} from './keypair.ts';
import { fromBase64, importAesKey, toBase64 } from './primitives.ts';

/**
 * Der entsperrte Zustand einer Sitzung.
 *
 * Der private Schlüssel lebt ausschließlich hier, im Arbeitsspeicher der
 * Seite. Er wird nicht in localStorage, sessionStorage oder IndexedDB
 * abgelegt – ein Neuladen der Seite verlangt deshalb bewusst eine erneute
 * Passworteingabe. Beim Abmelden wird er verworfen (Konzept 3.10 Punkt 4).
 *
 * Seit dem Umbau auf ein einziges Passwort (siehe kdf.ts) entstehen
 * Anmeldung und Entschlüsselung aus derselben Eingabe. Der Kunde gibt sein
 * Passwort einmal ein; der Server bekommt davon nur den Anmeldebeweis zu
 * sehen, niemals das Passwort und niemals den Verschlüsselungsschlüssel.
 */

export interface UnlockedVault {
  algorithm: KeyAlgorithm;
  privateKey: CryptoKey;
  publicKeyRaw: Uint8Array;
}

let unlocked: UnlockedVault | null = null;

export function currentVault(): UnlockedVault | null {
  return unlocked;
}

export function requireVault(): UnlockedVault {
  if (!unlocked) {
    throw new Error('Das Akten-Safe ist gesperrt. Bitte erneut anmelden.');
  }
  return unlocked;
}

/**
 * Die gerade entsperrte Organisation (Migration 007/010, Schlüsselkreise) —
 * höchstens eine gleichzeitig, wie die gewählte Organisation der Sitzung
 * selbst (`sessions.kunde_id`). Getrennt von `UnlockedVault`, weil sie
 * unabhängig davon existiert (ein Konto ohne Organisationsbezug hat nie
 * eine) und beim Organisationswechsel für sich neu aufgebaut wird, ohne den
 * Personen-Schlüssel anzutasten.
 */
export interface UnlockedOrganisation {
  kundeId: string;
  algorithm: KeyAlgorithm;
  privateKey: CryptoKey;
  publicKeyRaw: Uint8Array;
  /**
   * Rohe PKCS8-Bytes zusätzlich zum (nicht-exportierbaren) `privateKey` —
   * gebraucht, um den Organisationsschlüssel selbst weiterzuwrappen (Einladung
   * einer weiteren Person, `frontend/src/einladung.ts`). Lebt nur im
   * Arbeitsspeicher wie der Rest des entsperrten Zustands.
   */
  privateKeyPkcs8: Uint8Array;
}

let organisation: UnlockedOrganisation | null = null;

export function currentOrganisation(): UnlockedOrganisation | null {
  return organisation;
}

export function requireOrganisation(): UnlockedOrganisation {
  if (!organisation) {
    throw new Error(
      'Für diese Organisation ist noch kein Zugriff eingerichtet oder sie wurde noch nicht gewählt.',
    );
  }
  return organisation;
}

export function lockVault(): void {
  unlocked = null;
  organisation = null;
}

interface KeyMaterialResponse {
  algorithm: KeyAlgorithm;
  publicKey: string;
  encryptedPrivateKey: string;
  kdfParams: KdfParams;
  hasRecovery?: boolean;
}

interface PersonKeyMaterialResponse {
  algorithm: KeyAlgorithm;
  publicKey: string;
  encryptedPrivateKey: string;
  hasRecovery: boolean;
}

interface OrganisationKeyMaterialResponse {
  algorithm: KeyAlgorithm;
  publicKey: string;
  wrappedPrivateKey: string;
}

interface AuthResponse {
  token: string;
  account: {
    id: string;
    role: 'customer' | 'buddy' | 'admin';
    displayName: string;
    /** Migration 010/011 — nur bei role 'customer' und nur nach dem Schlüssel-Upgrade gesetzt. */
    personId?: string | null;
    kundeId?: string | null;
    orgRolle?: string | null;
  };
}

/**
 * Einmaliger Schlüssel-Upgrade: dasselbe Schlüsselpaar zusätzlich unter der
 * Person verpackt statt nur unter dem alten Konto (Abschnitt 3.4 des
 * Umsetzungsplans). Braucht die rohen PKCS8-Bytes und den
 * Verschlüsselungswert aus dem Passwort — beide nur im Moment des
 * Entsperrens verfügbar, deshalb nur von den drei Entsperrwegen unten
 * aufgerufen, nie beim bloßen Organisationswechsel.
 *
 * Wirkungslos für Buddy/Admin und Bestandskonten ohne Organisationsbezug
 * (`personId` fehlt) — die funktionieren unverändert wie vorher. Ein
 * Fehlschlag hier darf die eigentliche Anmeldung nicht zum Scheitern
 * bringen.
 */
async function sorgeFuerPersonSchluessel(
  personId: string | null | undefined,
  encryptionKey: CryptoKey,
  eigenerPrivateKeyPkcs8: Uint8Array,
  eigenerAlgorithm: KeyAlgorithm,
  eigenerPublicKeyRaw: Uint8Array,
): Promise<void> {
  if (!personId) return;
  try {
    await api.get<PersonKeyMaterialResponse>('/api/keys/person/me');
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error;
    await api.post('/api/keys/person/setup', {
      algorithm: eigenerAlgorithm,
      publicKey: toBase64(eigenerPublicKeyRaw),
      encryptedPrivateKey: toBase64(await wrapPrivateKey(eigenerPrivateKeyPkcs8, encryptionKey)),
    });
  }
}

/**
 * Entsperrt die angegebene Organisation mit dem bereits entsperrten
 * Personen-Schlüssel (`vault.privateKey`, nicht-exportierbar — deshalb reicht
 * hier der `CryptoKey`, anders als beim Personen-Upgrade oben, das noch die
 * rohen Bytes braucht). Aufrufbar sowohl direkt nach dem Login als auch nach
 * einem Organisationswechsel (`organisationen.ts`).
 *
 * Setzt bei fehlendem Zugriff `organisation` auf `null`, statt zu werfen —
 * eine Organisation ohne eigenen Zugriff (Einladung steht noch aus) ist ein
 * normaler Zustand, kein Fehler.
 */
export async function sorgeFuerOrganisationsSchluessel(kundeId: string | null | undefined): Promise<void> {
  organisation = null;
  if (!kundeId) return;
  const vault = requireVault();

  try {
    const material = await api.get<OrganisationKeyMaterialResponse>('/api/keys/organisation/me');
    const orgPrivateKeyPkcs8 = await unwrapBytes(
      fromBase64(material.wrappedPrivateKey),
      vault.privateKey,
      vault.algorithm,
    );
    organisation = {
      kundeId,
      algorithm: material.algorithm,
      privateKey: await importPrivateKey(orgPrivateKeyPkcs8, material.algorithm),
      publicKeyRaw: fromBase64(material.publicKey),
      privateKeyPkcs8: orgPrivateKeyPkcs8,
    };
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error;

    // Zwei mögliche Gründe für 404 (siehe backend/src/routes/keys.ts):
    //  - Für die Organisation existiert noch gar kein Schlüssel → als
    //    (vermutlich erstes) Mitglied jetzt anlegen.
    //  - Der Schlüssel existiert, nur nicht für mich gewrappt → das braucht
    //    eine Einladung durch ein bestehendes Mitglied; hier nichts tun,
    //    die Organisation bleibt gesperrt.
    if (error.message.includes('nicht für Sie gewrappt')) return;

    const neu = await generateKeyPair(vault.algorithm);
    const wrappedForSelf = await wrapBytesFor(neu.privateKeyPkcs8, vault.publicKeyRaw, vault.algorithm);
    await api.post('/api/keys/organisation/setup', {
      algorithm: neu.algorithm,
      publicKey: toBase64(neu.publicKeyRaw),
      wrappedPrivateKeyForSelf: toBase64(wrappedForSelf),
    });
    organisation = {
      kundeId,
      algorithm: neu.algorithm,
      privateKey: await importPrivateKey(neu.privateKeyPkcs8, neu.algorithm),
      publicKeyRaw: neu.publicKeyRaw,
      privateKeyPkcs8: neu.privateKeyPkcs8,
    };
  }
}

/**
 * Bündelt Personen-Upgrade und Organisations-Entsperrung nach dem
 * Freischalten des Tresors. Bewusst fehlertolerant: Schlägt das fehl, bleibt
 * der Akten-Safe im alten, weiterhin funktionierenden Zustand — nur ohne
 * Organisationszugriff. Die Anmeldung selbst ist zu diesem Zeitpunkt schon
 * abgeschlossen (Token gesetzt, `unlocked` steht).
 */
async function nachEntsperrenOrganisationHerstellen(
  account: AuthResponse['account'],
  encryptionKey: CryptoKey,
  privateKeyPkcs8: Uint8Array,
  algorithm: KeyAlgorithm,
  publicKeyRaw: Uint8Array,
): Promise<void> {
  if (account.role !== 'customer') return;
  try {
    await sorgeFuerPersonSchluessel(account.personId, encryptionKey, privateKeyPkcs8, algorithm, publicKeyRaw);
    await sorgeFuerOrganisationsSchluessel(account.kundeId);
  } catch (fehler) {
    console.warn('Organisation konnte nicht eingerichtet/entsperrt werden:', fehler);
  }
}

/**
 * Ersteinrichtung mit dem Code, den der Kunde von seinem Buddy bekommen hat.
 *
 * Zugang und Akten-Safe entstehen in einem Zug aus einer einzigen
 * Passworteingabe.
 */
export async function setupWithToken(options: {
  email: string;
  setupToken: string;
  password: string;
  role: 'customer' | 'buddy';
  withRecovery: boolean;
}): Promise<{ recoveryCode: string | null; account: AuthResponse['account'] }> {
  const masterKey = await deriveMasterKey(options.email, options.password);
  const authSecret = await deriveAuthSecret(masterKey);
  const encryptionKey = await deriveEncryptionKey(masterKey);

  const algorithm = await selectAlgorithm();
  const material = await generateKeyPair(algorithm);
  const encryptedPrivateKey = await wrapPrivateKey(material.privateKeyPkcs8, encryptionKey);

  let recoveryCode: string | null = null;
  let recoveryPrivateKey: string | undefined;
  let recoveryAuthSecret: string | undefined;

  if (options.withRecovery && options.role === 'customer') {
    recoveryCode = generateRecoveryCode();
    const rMaster = await deriveRecoveryMasterKey(options.email, recoveryCode);
    // Derselbe private Schlüssel, ein zweites Mal verpackt. Kein
    // Hintertür-Schlüssel: ohne den Code ist auch dieser Wert wertlos.
    recoveryPrivateKey = toBase64(
      await wrapPrivateKey(material.privateKeyPkcs8, await deriveRecoveryEncryptionKey(rMaster)),
    );
    recoveryAuthSecret = await deriveRecoveryAuthSecret(rMaster);
  }

  const response = await api.post<AuthResponse>('/api/auth/setup', {
    email: options.email,
    setupToken: options.setupToken.trim().toUpperCase(),
    authSecret,
    algorithm,
    publicKey: toBase64(material.publicKeyRaw),
    encryptedPrivateKey: toBase64(encryptedPrivateKey),
    kdfParams: DEFAULT_KDF_PARAMS,
    recoveryPrivateKey,
    recoveryAuthSecret,
  });

  setToken(response.token);
  unlocked = {
    algorithm,
    privateKey: await importPrivateKey(material.privateKeyPkcs8, algorithm),
    publicKeyRaw: material.publicKeyRaw,
  };
  await nachEntsperrenOrganisationHerstellen(
    response.account,
    encryptionKey,
    material.privateKeyPkcs8,
    algorithm,
    material.publicKeyRaw,
  );
  return { recoveryCode, account: response.account };
}

/**
 * Anmelden und entsperren in einem Schritt – der Kern des Umbaus.
 *
 * Vorher waren das zwei Eingaben: eine fürs Konto, eine für die Dokumente.
 * Jetzt genügt eine, ohne dass der Server mehr erfährt als vorher.
 */
export async function anmelden(
  email: string,
  password: string,
): Promise<AuthResponse['account']> {
  const masterKey = await deriveMasterKey(email, password);
  const authSecret = await deriveAuthSecret(masterKey);

  // Der Server bekommt ausschließlich den Anmeldebeweis zu sehen.
  const response = await api.post<AuthResponse>('/api/auth/login', {
    email,
    password: authSecret,
  });
  setToken(response.token);

  if (response.account.role === 'admin') {
    // Ein Admin hat keinen Akten-Safe und braucht kein Schlüsselmaterial.
    return response.account;
  }

  const rolle = response.account.role === 'buddy' ? 'buddy' : 'customer';
  const material = await api.get<KeyMaterialResponse>(`/api/keys/${rolle}/me`);
  const encryptionKey = await deriveEncryptionKey(masterKey);
  const privateKeyPkcs8 = await unwrapPrivateKey(
    fromBase64(material.encryptedPrivateKey),
    encryptionKey,
  );

  unlocked = {
    algorithm: material.algorithm,
    privateKey: await importPrivateKey(privateKeyPkcs8, material.algorithm),
    publicKeyRaw: fromBase64(material.publicKey),
  };
  await nachEntsperrenOrganisationHerstellen(
    response.account,
    encryptionKey,
    privateKeyPkcs8,
    material.algorithm,
    fromBase64(material.publicKey),
  );
  return response.account;
}

/**
 * Entsperren mit Schlüsseln, die das Kundenportal gereicht hat.
 *
 * Derselbe Vorgang wie `anmelden`, nur ohne Passworteingabe: Das Portal hat
 * das Passwort schon entgegengenommen und daraus dieselben zwei Werte
 * abgeleitet, die `anmelden` sich selbst ausrechnet. Beide Anwendungen
 * benutzen dafür bitgleich dieselbe Ableitung — festgehalten in den
 * Testvektoren in kdf.test.ts hier und in portal/src/lib/kdf.test.ts dort.
 *
 * Absichtlich als eigener Weg neben `anmelden` und nicht als Umbau davon:
 * Der Passwortweg ist der einzige, der ohne das Portal funktioniert, und er
 * soll von dieser Erweiterung nichts abbekommen.
 *
 * Das Schlüsselmaterial kommt als rohe Bytes und wird hier zum AES-Schlüssel
 * — der Übergabekanal transportiert keine CryptoKey-Objekte.
 */
export async function anmeldenMitUebergabe(
  email: string,
  authSecret: string,
  encryptionKeyRaw: Uint8Array,
): Promise<AuthResponse['account']> {
  const response = await api.post<AuthResponse>('/api/auth/login', {
    email,
    password: authSecret,
  });
  setToken(response.token);

  if (response.account.role === 'admin') {
    return response.account;
  }

  const rolle = response.account.role === 'buddy' ? 'buddy' : 'customer';
  const material = await api.get<KeyMaterialResponse>(`/api/keys/${rolle}/me`);
  const encryptionKey = await importAesKey(encryptionKeyRaw, ['encrypt', 'decrypt']);
  const privateKeyPkcs8 = await unwrapPrivateKey(
    fromBase64(material.encryptedPrivateKey),
    encryptionKey,
  );

  unlocked = {
    algorithm: material.algorithm,
    privateKey: await importPrivateKey(privateKeyPkcs8, material.algorithm),
    publicKeyRaw: fromBase64(material.publicKey),
  };
  await nachEntsperrenOrganisationHerstellen(
    response.account,
    encryptionKey,
    privateKeyPkcs8,
    material.algorithm,
    fromBase64(material.publicKey),
  );
  return response.account;
}

/**
 * Passwortwechsel im laufenden Betrieb (Etappe 3, 01.09.2026).
 *
 * Der private Schlüssel bleibt derselbe — er wird nur mit dem neuen
 * Verschlüsselungswert neu verpackt; die Dokumente und ihre DEKs sind nicht
 * betroffen. Der Wiederherstellungs-Code bleibt gültig: Sein Wrap hängt am
 * Code, nicht am Passwort (siehe setupWithToken).
 *
 * Das aktuelle Passwort wird doppelt geprüft: Erst hier lokal (öffnet der
 * alte Verschlüsselungswert den verpackten Schlüssel?), dann noch einmal
 * vom Server über den Anmeldebeweis — die Sitzung allein darf den
 * verpackten Schlüssel nicht überschreiben können.
 */
export async function passwortWechseln(options: {
  email: string;
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const altMaster = await deriveMasterKey(options.email, options.currentPassword);
  const altAuthSecret = await deriveAuthSecret(altMaster);
  const altEncryptionKey = await deriveEncryptionKey(altMaster);

  const material = await api.get<KeyMaterialResponse>('/api/keys/customer/me');
  let privateKeyPkcs8: Uint8Array;
  try {
    privateKeyPkcs8 = await unwrapPrivateKey(
      fromBase64(material.encryptedPrivateKey),
      altEncryptionKey,
    );
  } catch {
    throw new Error('Das aktuelle Passwort stimmt nicht.');
  }

  const neuMaster = await deriveMasterKey(options.email, options.newPassword);
  const neuAuthSecret = await deriveAuthSecret(neuMaster);
  const neuEncryptionKey = await deriveEncryptionKey(neuMaster);
  const neuVerpackt = toBase64(await wrapPrivateKey(privateKeyPkcs8, neuEncryptionKey));

  await api.post('/api/auth/passwort-wechsel', {
    currentAuthSecret: altAuthSecret,
    authSecret: neuAuthSecret,
    encryptedPrivateKey: neuVerpackt,
    // Dasselbe Schlüsselpaar liegt nach dem Schlüssel-Upgrade (Migration 010)
    // ein zweites Mal in person_keys — ohne diesen Mitzug würde der
    // Organisationszugriff nach dem Passwortwechsel nicht mehr aufgehen.
    // Harmlos, falls der Upgrade noch nie lief: Der Server aktualisiert dann
    // schlicht keine Zeile.
    encryptedPersonPrivateKey: neuVerpackt,
  });

  // Die entsperrte Sitzung bleibt gültig: Der private Schlüssel selbst hat
  // sich nicht geändert, nur seine Verpackung.
}

/**
 * Wiederherstellung nach vergessenem Passwort.
 *
 * Der Code öffnet den privaten Schlüssel; anschließend wird er mit dem neuen
 * Passwort neu verpackt und der Anmeldebeweis ausgetauscht. Die Dokumente
 * selbst bleiben unangetastet – ihre DEKs hängen am Schlüsselpaar, nicht am
 * Passwort.
 */
export async function wiederherstellen(options: {
  email: string;
  recoveryCode: string;
  newPassword: string;
}): Promise<void> {
  const rMaster = await deriveRecoveryMasterKey(options.email, options.recoveryCode);
  const recoveryAuthSecret = await deriveRecoveryAuthSecret(rMaster);

  // Der Nachweis geht mit: Ohne ihn rückt der Server das verpackte
  // Schlüsselmaterial gar nicht erst heraus.
  const material = await api.post<{ recoveryPrivateKey: string }>(
    '/api/auth/recovery-material',
    { email: options.email, recoveryAuthSecret },
  );

  const privateKeyPkcs8 = await unwrapPrivateKey(
    fromBase64(material.recoveryPrivateKey),
    await deriveRecoveryEncryptionKey(rMaster),
  );

  const masterKey = await deriveMasterKey(options.email, options.newPassword);
  const authSecret = await deriveAuthSecret(masterKey);
  const encryptionKey = await deriveEncryptionKey(masterKey);

  await api.post('/api/auth/recover', {
    email: options.email,
    recoveryAuthSecret,
    authSecret,
    encryptedPrivateKey: toBase64(await wrapPrivateKey(privateKeyPkcs8, encryptionKey)),
  });
}
